import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { __config_invoice, State } from '../../reducers';
import { State as ConfigState } from '../../reducers/invoices/reducers-segments-config';
import { Observable, combineLatest, zip, of } from 'rxjs';
import { getSegmentsConfig, getCustomerConfigState } from '../../reducers';
import { BackendInvoiseService } from '../backend-invoice.service';
import { filter, switchMap, tap, map, shareReplay, concatMap, catchError } from 'rxjs/operators';
import { Config, JavaConfig } from '../../models/invoices/segments.config.model';
import { Receive } from '../../actions/invoices/actions-segments-config';
import { ModularInvoicesConfig } from '../../models/invoices/invoicesConfig.model';
import * as CoreActions from '@uxdf/direct-shared-lib';

@Injectable({
  providedIn: 'root'
})
export class SegmentsConfigService {
  private configState$: Observable<ConfigState>;
  private fetchedConfigState$: Observable<Config | any>;
  private config$: Observable<ConfigState>;

  constructor(private readonly store: Store<State>, private readonly backendInvoiseService: BackendInvoiseService) {}

  public getConfig(): Observable<ConfigState> {
    if (!this.config$) {
      this.init();
      this.config$ = combineLatest([this.fetchedConfigState$, this.configState$]).pipe(
        map(([_, configState]: [any, ConfigState]) => configState),
        shareReplay(1)
      );
    }

    return this.config$;
  }

  private getStoreCodeSelector(): Observable<string> {
    return this.store.pipe(
      select(getCustomerConfigState),
      filter(({ isLoading }) => !isLoading),
      map(state => (state ? state.customerConfig.default_store_code : ''))
    );
  }

  private getFetchedConfig(): Observable<Config | any> {
    return zip(this.backendInvoiseService.getJavaConfig(), this.store.pipe(select(__config_invoice))).pipe(
      map(([javaConfig, configInvoice]: [JavaConfig, ModularInvoicesConfig]) => ({
        ...javaConfig,
        ...configInvoice.config
      })),
      catchError(error => {
        if (error && error.error !== undefined && (+error.error.code === 40 || +error.error.code === 42)) {
          return of(new CoreActions.DirectUserRoleHasBeenChanged());
        }
      })
    );
  }

  private init() {
    this.configState$ = this.store.pipe(select(getSegmentsConfig));
    this.fetchedConfigState$ = this.configState$.pipe(
      filter(config => config.updateRequired),
      switchMap(this.getStoreCodeSelector.bind(this)),
      concatMap(this.getFetchedConfig.bind(this)),
      catchError(error => {
        if (error && error.error !== undefined && (+error.error.code === 40 || +error.error.code === 42)) {
          return of(new CoreActions.DirectUserRoleHasBeenChanged());
        }

        this.store.dispatch(new Receive({ config: {} as Config, errorMessage: error.message }));
        return this.fetchedConfigState$;
      }),
      tap((response: Config) => {
        this.store.dispatch(new Receive({ config: response, errorMessage: '' }));
      })
    );
  }
}
